import { useCallback, useEffect, useRef, useState } from "react";
import {
  whatsappNumberReplacementService,
  type ReplaceNumberStatusResponse,
} from "@/services/whatsappNumberReplacementService";
import type { ApiError } from "@/types/api";
import { describeReplacement, type ReplacementPresentation } from "./replacementStatusModel";

/**
 * Owns "what does Atlas currently say about this channel's WhatsApp number
 * replacement" — and nothing else.
 *
 * ## Why this is a fetch, not a piece of client state
 *
 * A replacement is durable in Atlas and routinely outlives the browser tab
 * that started it. So this hook makes exactly one guarantee: on mount it asks
 * Atlas, and every value it exposes is derived from that answer. There is no
 * localStorage, no sessionStorage, no store slice, and no "I started one a
 * moment ago" flag — refresh, navigate away and back, or open the page on a
 * different machine, and the reconstruction is identical because it is the
 * same GET.
 *
 * ## Polling
 *
 * Polls only while {@link ReplacementPresentation.shouldPoll} is true, i.e.
 * while Atlas is expected to move on its own (or the status is unrecognised,
 * which polls defensively). Terminal and operator-action states — including
 * CHATWOOT_DISPLAY_PENDING — stop the timer. A channel with no replacement is
 * fetched once and then left alone, so a page listing many channels does not
 * turn into a polling storm.
 *
 * ## Connectivity vs. failure
 *
 * A failed GET is a *frontend connectivity* problem, never evidence about the
 * replacement. The last known operation is retained, `connectionError` is
 * raised for the banner, and polling continues so a reconnect self-heals. The
 * hook never synthesises a failed/completed status from a transport error.
 */

const DEFAULT_INTERVAL_MS = 5_000;

export interface UseWhatsappReplacementStatusResult {
  /** Latest replacement Atlas reports for the channel, or null if none ever. */
  operation: ReplaceNumberStatusResponse | null;
  presentation: ReplacementPresentation | null;
  /** True until the first answer (or first failure) has arrived. */
  loading: boolean;
  /** True once at least one fetch has settled — success or failure. */
  initialised: boolean;
  /** Set when the last fetch could not reach Atlas. Not a replacement failure. */
  connectionError: ApiError | null;
  /** Force an immediate re-read. Resolves with what Atlas returned. */
  refresh: () => Promise<ReplaceNumberStatusResponse | null>;
  /**
   * Seed the hook with a body Atlas returned from POST /replace-number or
   * /retry-chatwoot-display. Same DTO, same authority — this is not an
   * optimistic guess, it is a server response arriving through a different
   * call. Restarts polling if the new state warrants it.
   */
  applyServerResponse: (op: ReplaceNumberStatusResponse) => void;
}

export function useWhatsappReplacementStatus(
  channelId: string | null | undefined,
  options?: { enabled?: boolean; intervalMs?: number },
): UseWhatsappReplacementStatusResult {
  const enabled = (options?.enabled ?? true) && !!channelId;
  const intervalMs = options?.intervalMs ?? DEFAULT_INTERVAL_MS;

  const [operation, setOperation] = useState<ReplaceNumberStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialised, setInitialised] = useState(false);
  const [connectionError, setConnectionError] = useState<ApiError | null>(null);

  /** Guards every async write: flipped on unmount and on channel change. */
  const aliveRef = useRef(true);
  const timerRef = useRef<number | null>(null);
  /** Read by the timer callback so it always sees the current status. */
  const shouldPollRef = useRef(false);
  const inFlightRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const fetchOnce = useCallback(async (): Promise<ReplaceNumberStatusResponse | null> => {
    if (!channelId) return null;
    // Overlapping reads would let an older response win. One at a time.
    if (inFlightRef.current) return null;
    inFlightRef.current = true;
    try {
      const data = await whatsappNumberReplacementService.getStatus(channelId);
      if (!aliveRef.current) return data;
      setOperation(data);
      setConnectionError(null);
      shouldPollRef.current = describeReplacement(data)?.shouldPoll ?? false;
      return data;
    } catch (err) {
      if (!aliveRef.current) return null;
      // Transport/API problem — deliberately does NOT clear `operation` and
      // deliberately does NOT invent a status. Keep polling so a reconnect
      // repairs the view on its own.
      setConnectionError(err as ApiError);
      // eslint-disable-next-line no-console
      console.warn("[wa-number-replace] status poll failed for channel", channelId, err);
      return null;
    } finally {
      inFlightRef.current = false;
      if (aliveRef.current) {
        setLoading(false);
        setInitialised(true);
      }
    }
  }, [channelId]);

  /** (Re)arms the timer if — and only if — the current state warrants polling. */
  const scheduleNext = useCallback(() => {
    clearTimer();
    if (!aliveRef.current || !enabled || !shouldPollRef.current) return;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void fetchOnce().finally(scheduleNext);
    }, intervalMs);
  }, [clearTimer, enabled, fetchOnce, intervalMs]);

  const refresh = useCallback(async (): Promise<ReplaceNumberStatusResponse | null> => {
    const data = await fetchOnce();
    scheduleNext();
    return data;
  }, [fetchOnce, scheduleNext]);

  const applyServerResponse = useCallback(
    (op: ReplaceNumberStatusResponse) => {
      if (!aliveRef.current) return;
      setOperation(op);
      setConnectionError(null);
      setInitialised(true);
      setLoading(false);
      shouldPollRef.current = describeReplacement(op)?.shouldPoll ?? false;
      scheduleNext();
    },
    [scheduleNext],
  );

  // Initial read + timer lifecycle. Re-runs on channel change, and the cleanup
  // both kills the timer and invalidates in-flight responses so a late
  // resolution cannot write into an unmounted or re-keyed component.
  useEffect(() => {
    aliveRef.current = true;
    if (!enabled) {
      setOperation(null);
      setInitialised(false);
      shouldPollRef.current = false;
      return () => {
        aliveRef.current = false;
        clearTimer();
      };
    }
    setLoading(true);
    void fetchOnce().finally(scheduleNext);
    return () => {
      aliveRef.current = false;
      clearTimer();
    };
    // fetchOnce/scheduleNext are re-created per render by design; keying the
    // effect on them would restart polling on every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, enabled]);

  // Returning to a backgrounded tab is the common "came back later" path;
  // re-read immediately rather than waiting out the interval. Only while
  // something is actually in flight, so idle channels stay quiet.
  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (!shouldPollRef.current) return;
      void fetchOnce().finally(scheduleNext);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, channelId]);

  return {
    operation,
    presentation: describeReplacement(operation),
    loading,
    initialised,
    connectionError,
    refresh,
    applyServerResponse,
  };
}
